import { Grid } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useParams } from "react-router";
import useClientContext from "../../../../../../context/useClientContext";
import { getOrganizationReportTemplatesUrl } from "../../../report-lists/reports/utilities/editReportUrl";
import { useEnhancedBiApiClientProvider } from "../../contexts/ApiClientProviderContext";
import { useBiClientContext } from "../../contexts/ClientContext";
import { useApplyTemplate } from "../../hooks/useApplyTemplate";
import { useDefineEditReportData } from "../../hooks/useDefineEditReportData";
import { selectHasCurrentReport } from "../../store/selectors/currentReportSelectors";
import type { AppDispatch } from "../../store/store";
import { loadCompanyReportTemplates } from "../../store/thunks/reportTemplatesThunks";
import ApplyingDialog from "../common/dialogs/ApplyingDialog";
import ReportLoader from "../common/ReportLoader";
import TemplateNotFound from "../template/TemplateNotFound";
import ErrorApplyingTemplateDialog from "../template/dialogs/ErrorApplyingTemplateDialog";
import EditReportPage from "./EditReportPage";
import ReportNotFoundOrAccessForbidden from "./ReportNotFoundOrAccessForbidden";
import EditCommonDataProvider from "./common/EditCommonDataProvider";
import DrillDownEntry from "./common/drillDown/DrillDownEntry";
import useLedgerBasis from "./common/hooks/useLedgerBasis";

export const EditRoot = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { clientCode } = useClientContext();
  const { clientInfo } = useBiClientContext();
  const hasReport = useSelector(selectHasCurrentReport);
  const { showReportNotFound, isSwitchingReport, isFetching } = useDefineEditReportData(id ?? "");
  const { getReportsUrl } = useEnhancedBiApiClientProvider();
  useLedgerBasis();

  const {
    loadingTemplate,
    applyingTemplateName,
    showTemplateFailedToApply,
    showTemplateNotFound,
    handleCloseFailedToApplyDialog,
    handleTryAgain,
  } = useApplyTemplate();

  useEffect(() => {
    if (!clientInfo?.organization) return;
    dispatch(loadCompanyReportTemplates());
  }, [clientInfo?.organization, dispatch]);

  if (id === undefined) {
    return <Navigate to={""} />;
  }

  if (showReportNotFound === true) {
    return <ReportNotFoundOrAccessForbidden getReportsUrl={getReportsUrl} />;
  }

  if (showTemplateNotFound === true) {
    return <TemplateNotFound backUrl={getOrganizationReportTemplatesUrl(clientCode)} />;
  }

  const isFetchingEditReport = !isSwitchingReport && isFetching;

  if (!hasReport || isFetchingEditReport) {
    return <ReportLoader text="Loading metadata, it won't take long..." />;
  }

  return (
    <EditCommonDataProvider>
      <Grid container sx={{ bgcolor: "background.paper", width: "100%" }}>
        <EditReportPage />
        <DrillDownEntry reportId={id} />
        {loadingTemplate && <ApplyingDialog open text="Applying template..." />}
        {isSwitchingReport && <ApplyingDialog open text="Loading report..." />}
        {showTemplateFailedToApply && (
          <ErrorApplyingTemplateDialog
            open
            templateName={applyingTemplateName || "The"}
            onClose={handleCloseFailedToApplyDialog}
            onTryAgain={handleTryAgain}
          />
        )}
      </Grid>
    </EditCommonDataProvider>
  );
};
